/**
    * @description      : 
    * @author           : 
    * @group            : 
    * @created          : 26/08/2025 - 13:39:34
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 26/08/2025
    * - Author          : 
    * - Modification    : 
**/

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store";
import { useNavigate,useParams } from "react-router-dom";
import { Button } from "react-bootstrap";
import { FaPlay } from "react-icons/fa";
import { toast } from "react-toastify";
import { auth, db } from "../../../firebase";
import {
  doc,
  setDoc,
  updateDoc,
  increment,
  deleteDoc,
  getDoc
} from "firebase/firestore";
import { postData } from "../../../utils/axios";
import IconWrapper from "../../../utils/IconWrapper";
import { useTranslation } from 'react-i18next';


// Interfaces for consolidated data structures
interface ConsolidatedTiltedPVArea {
  polygon?: Array<{lat: number, lng: number}>;
  azimuth?: {
    value: number | null;
    isAutoCalculated: boolean;
  };
  elevation?: {
    value: number | null;
    isAutoCalculated: boolean;
    apiValue: number | null;
    manualValue: number | null;
  };
  heights?: {
    upper: number | null;
    lower: number | null;
  };
  slope?: number | null;
  edgeChecked?: string | null;
  swapped?: boolean;
  pointElevations?: number[];
}

interface ConsolidatedVerticalPVArea {
  polygon?: Array<{lat: number, lng: number}>;
  azimuth?: {
    value: number | null;
    isAutoCalculated: boolean;
  };
  elevation?: {
    value: number | null;
    isAutoCalculated: boolean;
    apiValue: number | null;
    manualValue: number | null;
  };
  heights?: {
    upper: number | null;
    lower: number | null;
  };
  slope?: number | null;
  swapped?: boolean;
  elevationData?: any | null;
}

// Original interfaces for API compatibility
interface PolygonPoint {
  lat: number;
  lon: number;
}

interface SimulationParameter {
  grid_width: string | number;
  resolution: string | number;
  sun_elevation_threshold: string | number;
  beam_spread: string | number;
  sun_angle: string | number;
  sun_reflection_threshold: string | number;
  intensity_threshold: string | number;
  moduleType: string | number;
}

interface ExcludeAreaPoint {
  lat: number;
  lng: number;
}

const CalculateGlare: React.FC = () => {
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [tiltedPVAreasData, setTiltedPVAreasData] = useState<{[key: string]: ConsolidatedTiltedPVArea}>({});
  const [verticalPVAreasData, setVerticalPVAreasData] = useState<{[key: string]: ConsolidatedVerticalPVArea}>({});
  
  const { projectId } = useParams<{ projectId: string | any }>();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  
  // Redux selectors remain the same
  const tiltedPolygons = useSelector((state: RootState) => state.bmap.tiltedPolygons);
  const tiltedIntersection = useSelector((state: RootState) => state.bmap.tiltedIntersection);
  const verticalPolygon = useSelector((state: RootState) => state.bmap.verticalPolygons);
  const tiltedUpperHeight = useSelector((state: RootState) => state.bmap.tiltedupperHeights);
  const tiltedLowerHeight = useSelector((state: RootState) => state.bmap.tiltedlowerHeights);
  const tiltedAzimuth = useSelector((state: RootState) => state.bmap.tiltedAzimuthValue);
  const detectionPoint = useSelector((state: RootState) => state.bmap.detectionPoints);
  const detectionPointHeight = useSelector((state: RootState) => state.bmap.detectionPointsHeight);
  const verticalSlope = useSelector((state: RootState) => state.bmap.verticalSlopeArea);
  const verticalLowerHeight = useSelector((state: RootState) => state.bmap.verticalLowerHeight);
  const verticalUpperHeight = useSelector((state: RootState) => state.bmap.verticalUpperHeight);
  const { t } = useTranslation(['mapPage', 'common']);

  const projectName = localStorage.getItem("projectName");
  const excludeArea = useSelector((state: RootState) => state.bmap.excludeArea);
  const Slope = useSelector((state: RootState) => state.bmap.slopeArea);
  const zoomLevel = useSelector((state: RootState) => state.bmap.zoom);
  const rulerLine = useSelector((state: RootState) => state.bmap.rulerLines);
  const center = useSelector((state: RootState) => state.bmap.center);
  const verticalAvgElevation = useSelector((state: RootState) => state.bmap.verticalAverageElevation);
  const tiltedAvgElevation = useSelector((state: RootState) => state.bmap.tiltedAverageElevation);
  const verticalAzimuth = useSelector((state: RootState) => state.bmap.verticalAzimuthValue);
  const simulationParameter = useSelector((state: RootState) => state.bmap.simulationParameter);
  const tiltedCheckedElevation = useSelector((state: RootState) => state.bmap.tiltedElevationChecked);
  const tiltedCheckedAzimuth = useSelector((state: RootState) => state.bmap.tiltedAzimuthChecked);
  const tiltedCheckedIndex = useSelector((state: RootState) => state.bmap.tiltedEdgeChecked);
  const verticalCheckedAzimuth = useSelector((state: RootState) => state.bmap.verticalAzimuthChecked);
  const verticalCheckedElevation = useSelector((state: RootState) => state.bmap.verticalElevationChecked);
  const verticalSwap = useSelector((state: RootState) => state.bmap.verticalSwap);
  const tiltedSwap = useSelector((state: RootState) => state.bmap.tiltedSwap);
  const [formValidationErrors, setFormValidationErrors] = useState<boolean>(false);

  const language = localStorage.getItem("i18nextLng");

  // Load consolidated data from Firebase
  useEffect(() => {
    const loadConsolidatedData = async () => {
      if (!projectId) {
        console.warn("Project ID not available, cannot load polygon data");
        return;
      }
      
      setIsLoading(true);
      
      try {
        // Load tilted PV areas from the new consolidated structure
        const loadTiltedPVAreas = async () => {
          try {
            const projectRef = doc(db, 'projects', projectId);
            const projectDoc = await getDoc(projectRef);
            
            if (projectDoc.exists()) {
              const data = projectDoc.data();
              return data.tiltedPVAreas || {};
            }
            
            return {};
          } catch (error) {
            console.error("Error loading tilted PV areas:", error);
            return {};
          }
        };
        
        // Load vertical PV areas from the new consolidated structure
        const loadVerticalPVAreas = async () => {
          try {
            const projectRef = doc(db, 'projects', projectId);
            const projectDoc = await getDoc(projectRef);
            
            if (projectDoc.exists()) {
              const data = projectDoc.data();
              return data.verticalPVAreas || {};
            }
            
            return {};
          } catch (error) {
            console.error("Error loading vertical PV areas:", error);
            return {};
          }
        };
        
        // Load data in parallel
        const [tiltedAreas, verticalAreas] = await Promise.all([
          loadTiltedPVAreas(),
          loadVerticalPVAreas()
        ]);
        
        // Update state with loaded data
        setTiltedPVAreasData(tiltedAreas);
        setVerticalPVAreasData(verticalAreas);
        
        setIsLoading(false);
      } catch (error) {
        console.error("Error loading polygon data:", error);
        setIsLoading(false);
      }
    };
    
    loadConsolidatedData();
  }, [projectId]);
  
  const generateRandomCombination = () => {
    const randomLetters = Array.from({ length: 7 }, () =>
      String.fromCharCode(Math.floor(Math.random() * 26) + 97)
    ).join("");
    const randomNumber = Math.floor(Math.random() * 100000);
    return randomLetters.toLocaleUpperCase() + randomNumber;
  };

  function getUtcOffset(timeZoneString: string) {
    const match = timeZoneString.match(/UTC([+-]\d+)/);
    return match ? match[1] : 0;
  }

  const date = new Date();
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  
  const formattedDateString = `${date.getDate()}. ${
    months[date.getMonth()]
  } ${date.getFullYear()} ${
    date.getHours() < 10 ? `0${date.getHours()}` : date.getHours()
  }:${date.getMinutes() < 10 ? `0${date.getMinutes()}` : date.getMinutes()}`;

  // Store data in Firebase
  const storeDatainDB = async () => {
    const userDocRef = doc(db, "projects", projectId);
    await updateDoc(userDocRef, {
      calculated: true,
      detectionPoint: JSON.stringify(detectionPoint),
      detectionPointHeight,
      tiltedCheckedIndex,
      tiltedCheckedAzimuth,
      excludeArea: JSON.stringify(excludeArea),
      rulerLine,
      mapCenter: center,
      totalCalcualetdReports: increment(1),
    });
  };

  // Start simulation - updated to prevent duplicate API calls
  const startTiltedSimulation = async (): Promise<void> => {
    // Prevent multiple executions
    if (isLoading) return;
    
    setIsLoading(true);
    const simValue = generateRandomCombination();
    
    try {
      // First save current project data
      await storeDatainDB();
      
      // Get the project document directly to ensure we have the latest data
      const projectRef = doc(db, 'projects', projectId);
      const projectDoc = await getDoc(projectRef);
      
      if (!projectDoc.exists()) {
        throw new Error("Project document not found");
      }
      
      const projectData = projectDoc.data();

      // Get detection point elevation data
      const detectionPointElevation = projectData.detectionPointElevation || {};

      // Get the UTC offset from Firebase (not localStorage)
      const timeZone = projectData.timeZone || "UTC+0 (GMT, WET)";
      const utcOffset = getUtcOffset(timeZone);
      
      // Process tilted polygons using the new consolidated structure
      const tiltedPVAreas = tiltedPolygons
        .filter((polygon, index) => 
          polygon && Array.isArray(polygon) && polygon.length === 4 && 
          tiltedAzimuth[index] !== null && 
          tiltedLowerHeight[index] !== null && 
          tiltedUpperHeight[index] !== null)
        .map((polygon, index) => {
          // Get area ID for the consolidated structure
          const areaId = `TPV-${index + 1}`;
          
          // Get consolidated data for this area
          const consolidatedData = tiltedPVAreasData[areaId] || {};
          
          // Get the elevation value - prioritize consolidated data
          let actualElevation = tiltedAvgElevation[index] || 0;
          
          // If we have consolidated data, use it
          if (consolidatedData.elevation) {
            const isAutoCalculated = consolidatedData.elevation.isAutoCalculated;
            actualElevation = isAutoCalculated 
              ? (consolidatedData.elevation.apiValue || actualElevation)
              : (consolidatedData.elevation.manualValue || consolidatedData.elevation.apiValue || actualElevation);
          }
          
          // Create the area points
          return [
            {
              latitude: Number((polygon[0]?.lat || 0).toFixed(6)),
              longitude: Number((polygon[0]?.lng || 0).toFixed(6)),
              ground_elevation: Number(actualElevation),
              height_above_ground: Number(tiltedUpperHeight[index] || 0),
            },
            {
              latitude: Number((polygon[1]?.lat || 0).toFixed(6)),
              longitude: Number((polygon[1]?.lng || 0).toFixed(6)),
              ground_elevation: Number(actualElevation),
              height_above_ground: Number(tiltedUpperHeight[index] || 0),
            },
            {
              latitude: Number((polygon[2]?.lat || 0).toFixed(6)),
              longitude: Number((polygon[2]?.lng || 0).toFixed(6)),
              ground_elevation: Number(actualElevation),
              height_above_ground: Number(tiltedLowerHeight[index] || 0),
            },
            {
              latitude: Number((polygon[3]?.lat || 0).toFixed(6)),
              longitude: Number((polygon[3]?.lng || 0).toFixed(6)),
              ground_elevation: Number(actualElevation),
              height_above_ground: Number(tiltedLowerHeight[index] || 0),
            },
          ];
        });
  
      // Process vertical polygons using the new consolidated structure
      const verticalPVAreas = verticalPolygon
        .filter((polygon, index) => 
          polygon && Array.isArray(polygon) && polygon.length === 2 && 
          verticalAzimuth[index] !== null && 
          verticalLowerHeight[index] !== null && 
          verticalUpperHeight[index] !== null)
        .map((polygon, index) => {
          // Get area ID for the consolidated structure
          const areaId = `VPV-${index + 1}`;
          
          // Get consolidated data for this area
          const consolidatedData = verticalPVAreasData[areaId] || {};
          
          // Get the elevation value - prioritize consolidated data
          let actualElevation = verticalAvgElevation[index] || 0;
          
          // If we have consolidated data, use it
          if (consolidatedData.elevation) {
            const isAutoCalculated = consolidatedData.elevation.isAutoCalculated;
            actualElevation = isAutoCalculated 
              ? (consolidatedData.elevation.apiValue || actualElevation)
              : (consolidatedData.elevation.manualValue || consolidatedData.elevation.apiValue || actualElevation);
          }
          
          // For vertical polygons, we create 4 points from 2 points
          return [
            {
              latitude: Number((polygon[0]?.lat || 0).toFixed(6)),
              longitude: Number((polygon[0]?.lng || 0).toFixed(6)),
              ground_elevation: Number(actualElevation),
              height_above_ground: Number(verticalUpperHeight[index] || 0),
            },
            {
              latitude: Number((polygon[1]?.lat || 0).toFixed(6)),
              longitude: Number((polygon[1]?.lng || 0).toFixed(6)),
              ground_elevation: Number(actualElevation),
              height_above_ground: Number(verticalUpperHeight[index] || 0),
            },
            {
              latitude: Number((polygon[1]?.lat || 0).toFixed(6)),
              longitude: Number((polygon[1]?.lng || 0).toFixed(6)),
              ground_elevation: Number(actualElevation),
              height_above_ground: Number(verticalLowerHeight[index] || 0),
            },
            {
              latitude: Number((polygon[0]?.lat || 0).toFixed(6)),
              longitude: Number((polygon[0]?.lng || 0).toFixed(6)),
              ground_elevation: Number(actualElevation),
              height_above_ground: Number(verticalLowerHeight[index] || 0),
            },
          ];
        });
  
      // Process tilted polygon information
      const listOfTiltedPvAreaInformation = tiltedPolygons 
        .filter((_, index) => tiltedAzimuth[index] !== null && Slope[index] !== null)
        .map((_, index) => {
          // Get area ID for the consolidated structure
          const areaId = `TPV-${index + 1}`;
          
          // Get consolidated data for this area
          const consolidatedData = tiltedPVAreasData[areaId] || {};
          
          // Get the azimuth value - prioritize consolidated data
          let azimuthValue = tiltedAzimuth[index] || 0;
          if (consolidatedData.azimuth && consolidatedData.azimuth.value !== null) {
            azimuthValue = consolidatedData.azimuth.value;
          }
          
          return {
            azimuth: Number(azimuthValue),
            tilt: Number(Slope[index] || 0),
            name: `PV Area ${index + 1}`,
          };
        });
  
      // Process vertical polygon information
      const listOfVerticalPvAreaInformation = verticalPolygon
        .filter((_, index) => verticalAzimuth[index] !== null && verticalSlope[index] !== null)
        .map((_, index) => {
          // Get area ID for the consolidated structure
          const areaId = `VPV-${index + 1}`;
          
          // Get consolidated data for this area
          const consolidatedData = verticalPVAreasData[areaId] || {};
          
          // Get the azimuth value - prioritize consolidated data
          let azimuthValue = verticalAzimuth[index] || 0;
          if (consolidatedData.azimuth && consolidatedData.azimuth.value !== null) {
            azimuthValue = consolidatedData.azimuth.value;
          }
          
          return {
            azimuth: Number(azimuthValue),
            tilt: Number(verticalSlope[index] || 90), // Default to 90 for vertical
            name: `PV Area ${listOfTiltedPvAreaInformation.length + index + 1}`,
          };
        });
  
      // Process detection points
      const listOfOps = detectionPoint
        .filter((point, index) => detectionPointHeight[index] !== null)
        .map((point, index) => {
          // Extract the actual point data (could be nested)
          const pointData = Array.isArray(point) ? point[0] : point;
          
          // Get elevation data from Firebase
          const elevationData = detectionPointElevation[index] || {};
          const isAutoCalculated = elevationData.isAutoCalculated !== undefined 
            ? elevationData.isAutoCalculated 
            : true;
          const elevationValue = isAutoCalculated 
            ? elevationData.apiValue 
            : (elevationData.manualValue !== null ? elevationData.manualValue : elevationData.apiValue);
          
          return {
            latitude: Number((pointData?.lat || 0).toFixed(6)),
            longitude: Number((pointData?.lng || 0).toFixed(6)),
            ground_elevation: Number((elevationValue || 0).toFixed(2)),
            height_above_ground: Number(detectionPointHeight[index] || 0),
          };
        });
  
      // Process exclude areas
      const excludedAreas = Array.isArray(excludeArea) 
        ? excludeArea
            .filter((innerArray: ExcludeAreaPoint[] | undefined) => Array.isArray(innerArray) && innerArray.length > 0)
            .map((innerArray: ExcludeAreaPoint[]) => {
              const firstElement = innerArray[0];
              const lastElement = innerArray[innerArray.length - 1];
              const adjustedArray = (firstElement && lastElement && 
                                    firstElement.lat === lastElement.lat && 
                                    firstElement.lng === lastElement.lng)
                ? innerArray.slice(0, -1) 
                : innerArray; 
              return adjustedArray
                .filter((value: ExcludeAreaPoint | undefined) => value && value.lat !== undefined && value.lng !== undefined)
                .map((value: ExcludeAreaPoint) => ({
                  latitude: value.lat,
                  longitude: value.lng,
                }));
            })
        : [];
      
      // Prepare the final data for simulation
      const raw = JSON.stringify({
        identifier: simValue,
        pv_areas: [...tiltedPVAreas, ...verticalPVAreas],
        list_of_pv_area_information: [
          ...listOfTiltedPvAreaInformation,
          ...listOfVerticalPvAreaInformation,
        ],
        list_of_ops: listOfOps,
        excluded_areas: excludedAreas,
        meta_data: {
          project_name: projectName || "Unnamed Project",
          user_id: auth.currentUser?.uid || "",
          project_id: projectId || "",
          sim_id: simValue,
          timestamp: Math.floor(Date.now() / 1000),
          utc: Number(utcOffset),
          language: language
        },
        
        simulation_parameter: {
          grid_width: parseFloat(((simulationParameter as SimulationParameter)?.grid_width || "0").toString().replace(',', '.')),
          resolution: `${(simulationParameter as SimulationParameter)?.resolution || "0"} min`,
          sun_elevation_threshold: parseFloat(((simulationParameter as SimulationParameter)?.sun_elevation_threshold || "0").toString().replace(',', '.')),
          beam_spread: parseFloat(((simulationParameter as SimulationParameter)?.beam_spread || "0").toString().replace(',', '.')),
          sun_angle: parseFloat(((simulationParameter as SimulationParameter)?.sun_angle || "0").toString().replace(',', '.')),
          sun_reflection_threshold: parseFloat(((simulationParameter as SimulationParameter)?.sun_reflection_threshold || "0").toString().replace(',', '.')),
          intensity_threshold: parseFloat(((simulationParameter as SimulationParameter)?.intensity_threshold || "0").toString().replace(',', '.')),
          module_type: parseFloat(((simulationParameter as SimulationParameter)?.moduleType || "0").toString().replace(',', '.'))
        },
      });
      // Create report document - use await to ensure it completes before navigation
      await setDoc(
        doc(db, "paidReport", simValue),
        {
          userId: auth.currentUser?.uid || "",
          displayName:auth.currentUser?.displayName || "",
          email:auth.currentUser?.email || "",
          paymentStatus: false,
          projectName: projectName || "Unnamed Project",
          verticalPVArea: verticalPolygon.length,
          tiltedPVArea: tiltedPolygons.length,
          detectionPoints: detectionPoint.length,
          uploadState: 0,
          fileUrl: false,
          date: formattedDateString || new Date().toISOString(),
          projectId: projectId || "",
        },
        { merge: true }
      );
      
      // Navigate to overview page
      navigate(`/projectOverview/${projectId}`, {
        state: { simulationId: simValue },
      });
      
      // Then make the API call
      await postData<any, typeof raw>("/generate_reports", raw, {
        headers: {
          "Content-Type": "application/json",
        },
      });
    } catch (error) {
      console.error("Failed to Retrieve PDF:", error);
      toast.error("Failed to Retrieve PDF: " + (error instanceof Error ? error.message : String(error)));
      try {
        const docRef = doc(db, "paidReport", simValue);
        await deleteDoc(docRef);
      } catch (deleteError) {
        console.error("Error deleting document:", deleteError);
      }
      navigate(`/map_page/${projectId}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  const isButtonDisabled =
    (tiltedPolygons.length === 0 && verticalPolygon.length === 0) ||
    detectionPoint.length === 0 ||
    tiltedIntersection ||
    isLoading;

  // Validation logic remains the same
  useEffect(() => {
    const handleButtonClick = () => {
      let allFieldsValid = true;
      const errors = [];
      
      if (tiltedIntersection) {
        setButtonDisabled(true);
        allFieldsValid = false;
        setErrorMessage("Tilted polygon is intersected the lines.");
      }
  
      if (tiltedPolygons.length === 0 && verticalPolygon.length === 0) {
        setButtonDisabled(true);
        allFieldsValid = false;
        setErrorMessage("You need to enter at least one PV area.");
      }
  
      if (detectionPoint.length === 0) {
        setButtonDisabled(true);
        allFieldsValid = false;
        setErrorMessage("You need to enter at least one detection point.");
      }
  
      // Check vertical polygon validation
      if (verticalPolygon.length > 0) {
        verticalPolygon.forEach((value, index) => {
          if (verticalLowerHeight[index] == null) {
            allFieldsValid = false;
            setButtonDisabled(true);
            setErrorMessage(
              `Vertical lower height of VPV-${index + 1} is required.`
            );
          }
          if (verticalUpperHeight[index] == null) {
            allFieldsValid = false;
            setButtonDisabled(true);
            setErrorMessage(
              `Vertical upper height of VPV-${index + 1} is required.`
            );
          }
          // Add height comparison validation for vertical areas
          if (verticalUpperHeight[index] != null && 
              verticalLowerHeight[index] != null && 
              Number(verticalUpperHeight[index]) < Number(verticalLowerHeight[index])) {
            allFieldsValid = false;
            setButtonDisabled(true);
            setErrorMessage(
              `Upper edge height must be greater than lower edge height in VPV-${index + 1}.`
            );
          }
          if (verticalAzimuth[index] == null) {
            allFieldsValid = false;
            setButtonDisabled(true);
            setErrorMessage(
              `Vertical azimuth of VPV-${index + 1} is required.`
            );
          }
          if (verticalSlope[index] == null) {
            allFieldsValid = false;
            setButtonDisabled(true);
            setErrorMessage(`Tile angle of VPV-${index + 1} is required.`);
          }
          if (verticalAvgElevation[index] == null) {
            allFieldsValid = false;
            setButtonDisabled(true);
            setErrorMessage(
              `Vertical average elevation of VPV-${index + 1} is required.`
            );
          }
        });
      }
  
      if (detectionPoint.length > 0) {
        detectionPoint.forEach((value, index) => {
          if (detectionPointHeight[index] == null) {
            allFieldsValid = false;
            setButtonDisabled(true);
            setErrorMessage(
              `Detection point Above ground of P-${index + 1} is required.`
            );
          }
        });
      }
  
      // Check tilted polygon validation
      if (tiltedPolygons.length > 0) {
        tiltedPolygons.forEach((value, index) => {
          if (tiltedLowerHeight[index] == null) {
            allFieldsValid = false;
            setButtonDisabled(true);
            setErrorMessage(
              `Tilted lower height of TPV-${index + 1} is required.`
            );
          }
          if (tiltedUpperHeight[index] == null) {
            allFieldsValid = false;
            setButtonDisabled(true);
            setErrorMessage(
              `Tilted upper height of TPV-${index + 1} is required.`
            );
          }
          // Add height comparison validation for tilted areas
          if (tiltedUpperHeight[index] != null && 
              tiltedLowerHeight[index] != null && 
              Number(tiltedUpperHeight[index]) < Number(tiltedLowerHeight[index])) {
            allFieldsValid = false;
            setButtonDisabled(true);
            setErrorMessage(
              `Upper edge height must be greater than lower edge height in TPV-${index + 1}.`
            );
          }
          if (Slope[index] == null) {
            allFieldsValid = false;
            setButtonDisabled(true);
            setErrorMessage(`Tile angle of TPV-${index + 1} is required.`);
          }
          if (tiltedAzimuth[index] == null) {
            allFieldsValid = false;
            setButtonDisabled(true);
            setErrorMessage(`Tilted azimuth of TPV-${index + 1} is required.`);
          }
          if (tiltedAvgElevation[index] == null) {
            allFieldsValid = false;
            setButtonDisabled(true);
            setErrorMessage(
              `Tilted elevation of TPV-${index + 1} is required.`
            );
          }
        });
      }
  
      if (allFieldsValid) {
        setButtonDisabled(false);
      }
    };
  
    handleButtonClick();
  }, [
    tiltedPolygons,
    verticalPolygon,
    detectionPoint,
    verticalLowerHeight,
    verticalUpperHeight,
    verticalSlope,
    verticalAzimuth,
    tiltedLowerHeight,
    tiltedIntersection,
    tiltedUpperHeight,
    tiltedAzimuth,
    Slope,
    detectionPointHeight,
    verticalAvgElevation,
    tiltedAvgElevation,
  ]);

  return (
    <Button
      className={buttonDisabled ? "disabledCalculateButton" : "CalculateButton"}
      onClick={() => {
        if (buttonDisabled) {
          toast.error(errorMessage);
        } else {
          startTiltedSimulation();
        }
      }}
      disabled={isLoading} // Explicitly disable button during loading
    >
      {isLoading ? (
        <span>Processing...</span>
      ) : (
        <>
          <IconWrapper Icon={FaPlay} size={15} color="#fff" /> {t('mapPage:calculation.calculationglare')}
        </>
      )}
    </Button>
  );
};

export default CalculateGlare;